import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Col,
  FormGroup,
  Input,
  Label,
  Form,
  FormFeedback,
  Alert,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { observer } from 'mobx-react-lite';
import { ErrorMessage, Formik } from 'formik';

import '../../FormComponents/styles.scss';
import '../styles.scss';

import getOrigin from '../../../utils/getOrigin';
import { END_USERS_ROLE } from '../../../utils/constants';
import EyeIcon from '../../../assets/eye.svg';
import EyeCrossedIcon from '../../../assets/eye-crossed.svg';
import { useStore } from '../../../store';

const CustomFormFeedback = ({ children }) => <FormFeedback className="styled-form-feedback">{children}</FormFeedback>;

const DeleteConfirmModal = observer(({ isOpen, toggle, logout }) => {
  const {
    rootStore: { profileStore },
  } = useStore();

  const [showPassword, setShowPassword] = useState(false);
  const initialValues = { currentPassword: '' };

  const { t } = useTranslation();

  const toggleShowPassword = () => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  };

  return (
    <Modal className="styled-modal" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader className="styled-header" toggle={toggle}>
        <span className="styled-modal-placeholder">{t('deleteConfirmModal.title')} </span>
      </ModalHeader>
      <ModalBody className="styled-modal-body">
        <Row>
          <Col className={'text-center'}>
            <h1>{t('deleteConfirmModal.description')}</h1>
          </Col>
        </Row>
        {profileStore.deleteStore.deleteError && (
          <Alert className="styled-alert" color="danger">
            {t('deleteConfirmModal.accountDeleteError')}
          </Alert>
        )}
        <Formik
          initialValues={initialValues}
          validate={values => {
            return profileStore.deleteStore.validateDelete(values, t);
          }}
          onSubmit={(values, { setSubmitting }) => {
            const isEndUser = profileStore.connection === END_USERS_ROLE;
            profileStore.deleteAccount(values).then(success => {
              setSubmitting(false);
              if (success) {
                toggle();
                logout({
                  returnTo: getOrigin(isEndUser),
                });
              }
            });
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <Form className="styled-form" onSubmit={handleSubmit}>
              <FormGroup>
                <span className="has-float-label">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="currentPassword"
                    id="password-current"
                    autoComplete="current-password"
                    placeholder={t('deleteConfirmModal.passwordPlaceholder')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.currentPassword}
                    invalid={!!errors.currentPassword && touched.currentPassword}
                  />
                  {values.currentPassword.length ? (
                    <span className="styled-input-icon" onClick={toggleShowPassword}>
                      <img className="styled-eye-icon" src={showPassword ? EyeIcon : EyeCrossedIcon} alt="eye-icon" />
                    </span>
                  ) : null}
                  <Label
                    for="password-current-modal"
                    className={!!errors.currentPassword && touched.currentPassword ? 'is-invalid' : ''}
                  >
                    {t('deleteConfirmModal.passwordLabel')}
                  </Label>
                  <ErrorMessage name="currentPassword" component={CustomFormFeedback} />
                </span>
              </FormGroup>
              <ModalFooter className="styled-modal-footer">
                <button className="modal-button" disabled={isSubmitting} type="button" onClick={toggle}>
                  {' '}
                  {t('deleteModal.cancelButton')}
                </button>
                <button
                  className={`modal-button primary`}
                  type="submit"
                  disabled={isSubmitting || Object.keys(errors).length !== 0}
                >
                  {' '}
                  {t('deleteConfirmModal.deleteButton')}
                </button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
});

export default DeleteConfirmModal;
